/**
 * Barrel re-export for the Guardian API client.
 *
 * WO-10.4 split this file (formerly ~3,470 lines holding every domain's
 * fetch calls in one `api` object literal) into per-domain modules under
 * `lib/api/`. This file exists ONLY to preserve the import shape every
 * caller already depends on:
 *
 *   import { api } from '@/lib/api'
 *   import { tcmApi } from '@/lib/api'
 *   import apiClient from '@/lib/api'          // default export
 *   import type { OrganizationListItemDto } from '@/lib/api'
 *   import { initiateSignUp } from '@/lib/api'
 *
 * `api` is assembled by spreading every domain module's exported object
 * (`dashboardApi`, `driftApi`, …) into one object literal — this preserves
 * the original single-object shape (`api.getDashboardStats(...)`,
 * `api.getConfigurationDrifts(...)`, etc.) with zero call-site churn.
 * Cross-domain calls that the original code made via `this.xxx(...)` (e.g.
 * `getDashboardStats` → `getDriftTrends` → `getConfigurationDrifts`) are
 * now direct function imports between modules instead of `this` — same
 * runtime call graph, but statically typed instead of relying on `this`
 * resolving against the eventual merged object (which TypeScript can't
 * check across separate object literals).
 *
 * New code should import directly from the domain module
 * (`@/lib/api/drift`, `@/lib/api/tcm`, etc.) rather than through this
 * barrel where practical — but importing through `@/lib/api` continues to
 * work and is not deprecated.
 */

import apiClient from './api/client'
import { dashboardApi } from './api/dashboard'
import { driftApi } from './api/drift'
import { reportsApi } from './api/reports'
import { complianceApi } from './api/compliance'
import { oauthApi } from './api/oauth'
import { notificationsApi } from './api/notifications'
import { tenantsApi } from './api/tenants'
import { adminApi } from './api/admin'
import { insightsApi } from './api/insights'

export const api = {
  ...dashboardApi,
  ...driftApi,
  ...reportsApi,
  ...complianceApi,
  ...oauthApi,
  ...notificationsApi,
  ...tenantsApi,
  ...adminApi,
  ...insightsApi,
}

export { tcmApi } from './api/tcm'

export * from './api/dashboard'
export * from './api/drift'
export * from './api/reports'
export * from './api/compliance'
export * from './api/oauth'
export * from './api/notifications'
export * from './api/tenants'
export * from './api/admin'
export * from './api/insights'
export * from './api/tcm'
export * from './api/signup'

export default apiClient
